/**
 * /releases — public feed of MeloCraft release notes. Every release note the
 * founder publishes lands here, newest first. Same HTML the emailed users get,
 * minus the email wrapper.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { Newspaper } from "lucide-react";

export default function Releases() {
  const [releases, setReleases] = useState(null);

  useEffect(() => {
    api.get("/releases?limit=30")
      .then((r) => setReleases(r.data.releases || []))
      .catch(() => setReleases([]));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 pb-32" data-testid="releases-page">
      <div className="flex items-center gap-3 mb-2">
        <Newspaper className="h-6 w-6 text-[#FF4500]" />
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500]">Release notes</div>
      </div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-3">What's new.</h1>
      <p className="font-mono text-sm text-gray-400 mb-10 max-w-xl">
        Every time we ship an update to MeloCraft, we post the change here and email everyone
        who hasn't opted out. No newsletter fluff — just what changed and why.
      </p>

      {releases === null && <div className="font-mono text-xs text-gray-500">Loading...</div>}
      {releases && releases.length === 0 && (
        <div className="border border-dashed border-[#2A2A2A] p-6 font-mono text-xs text-gray-500 italic">
          No releases published yet.
        </div>
      )}
      <div className="space-y-8">
        {(releases || []).map((r) => (
          <article
            key={r.id}
            data-testid={`release-article-${r.id}`}
            className="border-l-2 border-[#FF4500] pl-4 md:pl-6"
          >
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              {new Date(r.created_at).toLocaleDateString([], { year: "numeric", month: "long", day: "numeric" })}
            </div>
            <h2 className="font-display text-2xl md:text-3xl leading-tight mb-1">{r.title}</h2>
            {r.subtitle && (
              <div className="font-mono text-sm text-gray-400 mb-3">{r.subtitle}</div>
            )}
            <div
              className="prose prose-invert prose-sm md:prose-base max-w-none text-gray-200"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.body_html) }}
            />
          </article>
        ))}
      </div>
    </div>
  );
}
